import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Container, Form, Row, Table } from 'react-bootstrap';
import { Link, Navigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
  FaArrowLeft,
  FaBoxOpen,
  FaCalendarAlt,
  FaClipboardCheck,
  FaDumbbell,
  FaEdit,
  FaPlus,
  FaSave,
  FaSlidersH,
  FaTrash,
  FaUserTie,
  FaUsers,
} from 'react-icons/fa';
import Navigation from './Navigation';
import AdminPageHeader from './AdminPageHeader';
import {
  getAdminActorType,
  hasAnyAllowedPath,
  isManagementUser,
  readStoredAdminUser,
} from './adminAccessControl';
import './FitnessTransactionsManager.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const CLASS_SHARE_OPTIONS = [
  { value: 'percentage', label: 'Percentage' },
  { value: 'fixed_total', label: 'Fixed total' },
  { value: 'per_participant', label: 'Per participant' },
];

const PT_PRODUCT_TYPE_OPTIONS = [
  { value: 'package', label: 'Package' },
  { value: 'single_session', label: 'Single Session' },
];

const PT_SHARE_OPTIONS = [
  { value: 'percentage', label: 'Percentage' },
  { value: 'fixed_total', label: 'Fixed total' },
  { value: 'per_participant', label: 'Per participant' },
  { value: 'per_session', label: 'Per session' },
];

const formatCurrency = value =>
  `Rp ${Number(value || 0).toLocaleString('id-ID', {
    maximumFractionDigits: 0,
  })}`;

const isApiSuccess = data =>
  data && (data.error === 0 || data.error === '0' || typeof data.error === 'undefined');

function FitnessTransactionSetup() {
  const token = useMemo(() => localStorage.getItem('sessionToken') || '', []);
  const currentUser = useMemo(() => readStoredAdminUser(), []);
  const actorType = useMemo(() => getAdminActorType(currentUser), [currentUser]);
  const isScopedActor = actorType === 'outlet';
  const canAccess =
    isManagementUser(currentUser) && hasAnyAllowedPath('/fitness-transactions/setup', currentUser);
  const authHeaders = useMemo(
    () => ({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    }),
    [token]
  );

  const [outlets, setOutlets] = useState([]);
  const [selectedOutlet, setSelectedOutlet] = useState(
    isScopedActor ? String(currentUser.outlet_id || '') : ''
  );
  const [classes, setClasses] = useState([]);
  const [ptUsers, setPTUsers] = useState([]);
  const [ptProducts, setPTProducts] = useState([]);
  const [ptClients, setPTClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [classForm, setClassForm] = useState({
    id: null,
    class_name: '',
    coach_name: '',
    unit_price: '',
    coach_share_mode: 'percentage',
    coach_share_value: '',
    is_active: true,
    notes: '',
  });
  const [productForm, setProductForm] = useState({
    id: null,
    pt_user_id: '',
    product_name: '',
    product_type: 'package',
    session_count: '8',
    price: '',
    expiry_days: '30',
    share_mode: 'percentage',
    share_value: '',
    is_active: true,
    notes: '',
  });

  const parseApiResponse = useCallback(async response => {
    const raw = await response.text();
    if (!raw) {
      return {};
    }
    try {
      return JSON.parse(raw);
    } catch (error) {
      return { error: 1, message: raw };
    }
  }, []);

  const performRequest = useCallback(
    async (url, options, fallbackMessage) => {
      const response = await fetch(url, options);
      const data = await parseApiResponse(response);
      if (!response.ok || !isApiSuccess(data)) {
        throw new Error(data.message || fallbackMessage);
      }
      return data;
    },
    [parseApiResponse]
  );

  const productPTOptions = useMemo(
    () =>
      ptUsers.map(item => ({
        value: String(item.id),
        label: item.is_active ? item.pt_name : `${item.pt_name} (inactive)`,
      })),
    [ptUsers]
  );

  const fetchOutlets = useCallback(async () => {
    if (isScopedActor) {
      const scopedOutlet = currentUser.outlet_id
        ? [
            {
              id: String(currentUser.outlet_id),
              outlet_name: currentUser.outlet_name || 'Assigned outlet',
            },
          ]
        : [];
      setOutlets(scopedOutlet);
      if (scopedOutlet[0]?.id) {
        setSelectedOutlet(prev => prev || scopedOutlet[0].id);
      }
      return;
    }

    const data = await performRequest(
      `${API_BASE_URL}outletlist`,
      {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ limit: 200, offset: 0 }),
      },
      'Gagal mengambil outlet'
    );

    const normalized = (Array.isArray(data.results) ? data.results : []).map(item => ({
      id: String(item.id ?? item.ID ?? ''),
      outlet_name:
        item.outlet_name || item.OutletName || item.name || `Outlet #${item.id || item.ID}`,
    }));
    setOutlets(normalized);
    if (normalized[0]?.id) {
      setSelectedOutlet(prev => prev || normalized[0].id);
    }
  }, [authHeaders, currentUser, isScopedActor, performRequest]);

  const fetchClasses = useCallback(
    async outletId => {
      if (!outletId) {
        setClasses([]);
        return;
      }

      const data = await performRequest(
        `${API_BASE_URL}fitness-class/list`,
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            limit: 200,
            offset: 0,
            outlet_id: Number(outletId),
          }),
        },
        'Gagal mengambil class'
      );

      setClasses(Array.isArray(data.results) ? data.results : []);
    },
    [authHeaders, performRequest]
  );

  const fetchPTWorkspace = useCallback(
    async outletId => {
      if (!outletId) {
        setPTUsers([]);
        setPTProducts([]);
        setPTClients([]);
        return;
      }

      const [ptUserData, ptProductData, ptClientData] = await Promise.all([
        performRequest(
          `${API_BASE_URL}pt-user/list`,
          {
            method: 'POST',
            headers: authHeaders,
            body: JSON.stringify({ limit: 200, offset: 0 }),
          },
          'Gagal mengambil staff layanan'
        ),
        performRequest(
          `${API_BASE_URL}pt-product/list`,
          {
            method: 'POST',
            headers: authHeaders,
            body: JSON.stringify({
              limit: 200,
              offset: 0,
              outlet_id: Number(outletId),
            }),
          },
          'Gagal mengambil layanan session'
        ),
        performRequest(
          `${API_BASE_URL}pt-client/list`,
          {
            method: 'POST',
            headers: authHeaders,
            body: JSON.stringify({
              limit: 200,
              offset: 0,
              outlet_id: Number(outletId),
              is_active: true,
            }),
          },
          'Gagal mengambil session client'
        ),
      ]);

      setPTUsers(Array.isArray(ptUserData.results) ? ptUserData.results : []);
      setPTProducts(Array.isArray(ptProductData.results) ? ptProductData.results : []);
      setPTClients(Array.isArray(ptClientData.results) ? ptClientData.results : []);
    },
    [authHeaders, performRequest]
  );

  const resetForm = useCallback(() => {
    setClassForm({
      id: null,
      class_name: '',
      coach_name: '',
      unit_price: '',
      coach_share_mode: 'percentage',
      coach_share_value: '',
      is_active: true,
      notes: '',
    });
  }, []);

  const resetProductForm = useCallback(() => {
    setProductForm({
      id: null,
      pt_user_id: '',
      product_name: '',
      product_type: 'package',
      session_count: '8',
      price: '',
      expiry_days: '30',
      share_mode: 'percentage',
      share_value: '',
      is_active: true,
      notes: '',
    });
  }, []);

  useEffect(() => {
    if (!token || !canAccess) {
      return;
    }

    setLoading(true);
    fetchOutlets()
      .catch(error => Swal.fire('Error', error.message || 'Gagal memuat data setup', 'error'))
      .finally(() => setLoading(false));
  }, [canAccess, fetchOutlets, token]);

  useEffect(() => {
    if (!selectedOutlet) {
      return;
    }
    Promise.all([fetchClasses(selectedOutlet), fetchPTWorkspace(selectedOutlet)]).catch(error =>
      Swal.fire('Error', error.message || 'Gagal memuat setup outlet', 'error')
    );
  }, [fetchClasses, fetchPTWorkspace, selectedOutlet]);

  useEffect(() => {
    if (!productForm.pt_user_id) {
      return;
    }

    const stillExists = ptUsers.some(item => String(item.id) === String(productForm.pt_user_id));
    if (!stillExists) {
      setProductForm(prev => ({ ...prev, pt_user_id: '' }));
    }
  }, [productForm.pt_user_id, ptUsers]);

  const handleSubmit = async event => {
    event.preventDefault();
    try {
      await performRequest(
        `${API_BASE_URL}fitness-class/upsert`,
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            id: classForm.id,
            outlet_id: Number(selectedOutlet),
            class_name: classForm.class_name.trim(),
            coach_name: classForm.coach_name.trim(),
            unit_price: Number(classForm.unit_price || 0),
            coach_share_mode: classForm.coach_share_mode,
            coach_share_value: Number(classForm.coach_share_value || 0),
            is_active: classForm.is_active,
            notes: classForm.notes.trim(),
          }),
        },
        'Gagal menyimpan class'
      );

      Swal.fire('Success', 'Setup class berhasil disimpan', 'success');
      resetForm();
      await fetchClasses(selectedOutlet);
    } catch (error) {
      Swal.fire('Error', error.message || 'Gagal menyimpan class', 'error');
    }
  };

  const handleDelete = async id => {
    const confirm = await Swal.fire({
      title: 'Hapus class ini?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Hapus',
    });
    if (!confirm.isConfirmed) {
      return;
    }

    try {
      await performRequest(
        `${API_BASE_URL}fitness-class/delete`,
        {
          method: 'DELETE',
          headers: authHeaders,
          body: JSON.stringify({ id }),
        },
        'Gagal menghapus class'
      );
      Swal.fire('Deleted', 'Class berhasil dihapus', 'success');
      await fetchClasses(selectedOutlet);
    } catch (error) {
      Swal.fire('Error', error.message || 'Gagal menghapus class', 'error');
    }
  };

  const handleSubmitPTProduct = async event => {
    event.preventDefault();
    try {
      await performRequest(
        `${API_BASE_URL}${productForm.id ? 'pt-product/update' : 'pt-product/add'}`,
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            id: productForm.id,
            outlet_id: Number(selectedOutlet),
            pt_user_id: Number(productForm.pt_user_id || 0),
            product_name: productForm.product_name.trim(),
            product_type: productForm.product_type,
            session_count: Number(productForm.session_count || 0),
            price: Number(productForm.price || 0),
            expiry_days: Number(productForm.expiry_days || 0),
            share_mode: productForm.share_mode,
            share_value: Number(productForm.share_value || 0),
            is_active: productForm.is_active,
            notes: productForm.notes.trim(),
          }),
        },
        'Gagal menyimpan layanan session'
      );

      Swal.fire('Success', 'Layanan session berhasil disimpan', 'success');
      resetProductForm();
      await fetchPTWorkspace(selectedOutlet);
    } catch (error) {
      Swal.fire('Error', error.message || 'Gagal menyimpan layanan session', 'error');
    }
  };

  const handleDeletePTProduct = async id => {
    const confirm = await Swal.fire({
      title: 'Hapus layanan session ini?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Hapus',
    });
    if (!confirm.isConfirmed) {
      return;
    }

    try {
      await performRequest(
        `${API_BASE_URL}pt-product/delete`,
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({ id }),
        },
        'Gagal menghapus layanan session'
      );
      Swal.fire('Deleted', 'Layanan session berhasil dihapus', 'success');
      await fetchPTWorkspace(selectedOutlet);
    } catch (error) {
      Swal.fire('Error', error.message || 'Gagal menghapus layanan session', 'error');
    }
  };

  if (!canAccess) {
    return <Navigate to="/fitness-transactions" replace />;
  }

  return (
    <>
      <Navigation />
      <Container fluid className="admin-page-shell mt-4 px-4 pb-5">
        <AdminPageHeader
          icon={FaSlidersH}
          eyebrow="Program workspace"
          title="Program Setup"
          description="Kelola katalog program dan pantau layanan private session dalam outlet yang sama supaya front desk dan workspace layanan memakai referensi yang selaras."
          breadcrumbs={[
            { label: 'Dashboard', to: '/dashboardadmin' },
            { label: 'Front Desk', to: '/fitness-transactions' },
            { label: 'Program Setup' },
          ]}
          actions={
            <div className="d-flex flex-wrap gap-2">
              <Button as={Link} to="/fitness-transactions" variant="success">
                <FaArrowLeft /> Back to Front Desk
              </Button>
            </div>
          }
        />

        <Card className="sales-workspace__main-card mb-4">
          <Card.Body className="d-flex flex-wrap gap-2">
            <Button variant="dark" disabled>
              <FaSlidersH className="me-2" />
              Program Setup
            </Button>
            <Button
              as={Link}
              to="/fitness-transactions/setup/pt-setting"
              variant="outline-secondary"
            >
              <FaUserTie className="me-2" />
              Private Session Setup
            </Button>
          </Card.Body>
        </Card>

        <Row className="g-4">
          <Col xl={4}>
            <div className="d-grid gap-4">
              <Card className="sales-workspace__main-card">
                <Card.Body>
                  <div className="sales-workspace__side-title mb-3">
                    <FaDumbbell />
                    <span>Program catalog</span>
                  </div>

                  <Form.Group className="mb-3">
                    <Form.Label>Outlet</Form.Label>
                    <Form.Select
                      value={selectedOutlet}
                      onChange={event => setSelectedOutlet(event.target.value)}
                      disabled={isScopedActor}
                    >
                      <option value="">Pilih outlet</option>
                      {outlets.map(item => (
                        <option key={item.id} value={item.id}>
                          {item.outlet_name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label>Nama class</Form.Label>
                      <Form.Control
                        value={classForm.class_name}
                        onChange={event =>
                          setClassForm(prev => ({ ...prev, class_name: event.target.value }))
                        }
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Coach</Form.Label>
                      <Form.Control
                        value={classForm.coach_name}
                        onChange={event =>
                          setClassForm(prev => ({ ...prev, coach_name: event.target.value }))
                        }
                      />
                    </Form.Group>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Harga / orang</Form.Label>
                          <Form.Control
                            type="number"
                            min="0"
                            step="0.01"
                            value={classForm.unit_price}
                            onChange={event =>
                              setClassForm(prev => ({ ...prev, unit_price: event.target.value }))
                            }
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Mode share</Form.Label>
                          <Form.Select
                            value={classForm.coach_share_mode}
                            onChange={event =>
                              setClassForm(prev => ({ ...prev, coach_share_mode: event.target.value }))
                            }
                          >
                            {CLASS_SHARE_OPTIONS.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3">
                      <Form.Label>Nilai share coach</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        step="0.01"
                        value={classForm.coach_share_value}
                        onChange={event =>
                          setClassForm(prev => ({ ...prev, coach_share_value: event.target.value }))
                        }
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Check
                        type="switch"
                        label="Class aktif"
                        checked={classForm.is_active}
                        onChange={event =>
                          setClassForm(prev => ({ ...prev, is_active: event.target.checked }))
                        }
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Catatan</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={classForm.notes}
                        onChange={event =>
                          setClassForm(prev => ({ ...prev, notes: event.target.value }))
                        }
                      />
                    </Form.Group>

                    <div className="d-flex gap-2 flex-wrap">
                      <Button type="submit" variant="success">
                        {classForm.id ? <FaSave /> : <FaPlus />}{' '}
                        {classForm.id ? 'Update class' : 'Tambah class'}
                      </Button>
                      {classForm.id ? (
                        <Button type="button" variant="outline-success" onClick={resetForm}>
                          Reset form
                        </Button>
                      ) : null}
                    </div>
                  </Form>
                </Card.Body>
              </Card>

              <Card className="sales-workspace__main-card">
                <Card.Body>
                  <div className="sales-workspace__side-title mb-3">
                    <FaBoxOpen />
                    <span>Session service catalog</span>
                  </div>
                  <div className="text-muted small mb-3">
                    Setup layanan session dipusatkan di sini agar front desk dan workspace layanan memakai satu referensi yang sama.
                  </div>

                  <Form onSubmit={handleSubmitPTProduct}>
                    <Form.Group className="mb-3">
                      <Form.Label>Session staff</Form.Label>
                      <Form.Select
                        value={productForm.pt_user_id}
                        onChange={event =>
                          setProductForm(prev => ({ ...prev, pt_user_id: event.target.value }))
                        }
                        required
                      >
                        <option value="">Pilih staff layanan</option>
                        {productPTOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Nama product</Form.Label>
                      <Form.Control
                        value={productForm.product_name}
                        onChange={event =>
                          setProductForm(prev => ({ ...prev, product_name: event.target.value }))
                        }
                        required
                      />
                    </Form.Group>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Tipe</Form.Label>
                          <Form.Select
                            value={productForm.product_type}
                            onChange={event =>
                              setProductForm(prev => ({ ...prev, product_type: event.target.value }))
                            }
                          >
                            {PT_PRODUCT_TYPE_OPTIONS.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Check
                            className="mt-4"
                            type="switch"
                            label="Product aktif"
                            checked={productForm.is_active}
                            onChange={event =>
                              setProductForm(prev => ({ ...prev, is_active: event.target.checked }))
                            }
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Jumlah sesi</Form.Label>
                          <Form.Control
                            type="number"
                            min="1"
                            value={productForm.session_count}
                            onChange={event =>
                              setProductForm(prev => ({ ...prev, session_count: event.target.value }))
                            }
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Harga</Form.Label>
                          <Form.Control
                            type="number"
                            min="0"
                            step="0.01"
                            value={productForm.price}
                            onChange={event =>
                              setProductForm(prev => ({ ...prev, price: event.target.value }))
                            }
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Expiry days</Form.Label>
                          <Form.Control
                            type="number"
                            min="0"
                            value={productForm.expiry_days}
                            onChange={event =>
                              setProductForm(prev => ({ ...prev, expiry_days: event.target.value }))
                            }
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Mode share</Form.Label>
                          <Form.Select
                            value={productForm.share_mode}
                            onChange={event =>
                              setProductForm(prev => ({ ...prev, share_mode: event.target.value }))
                            }
                          >
                            {PT_SHARE_OPTIONS.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Nilai share</Form.Label>
                          <Form.Control
                            type="number"
                            min="0"
                            step="0.01"
                            value={productForm.share_value}
                            onChange={event =>
                              setProductForm(prev => ({ ...prev, share_value: event.target.value }))
                            }
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3">
                      <Form.Label>Catatan</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={productForm.notes}
                        onChange={event =>
                          setProductForm(prev => ({ ...prev, notes: event.target.value }))
                        }
                      />
                    </Form.Group>

                    <div className="d-flex gap-2 flex-wrap">
                      <Button type="submit" variant="success">
                        {productForm.id ? <FaSave /> : <FaPlus />}{' '}
                        {productForm.id ? 'Update product' : 'Tambah product'}
                      </Button>
                      {productForm.id ? (
                        <Button type="button" variant="outline-success" onClick={resetProductForm}>
                          Reset form
                        </Button>
                      ) : null}
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </div>
          </Col>

          <Col xl={8}>
            <Row className="g-4">
              <Col md={6}>
                <Card className="sales-workspace__side-card h-100">
                  <Card.Body>
                    <div className="sales-workspace__side-title">
                      <FaClipboardCheck />
                      <span>Membership setup</span>
                    </div>
                    <p className="text-muted mb-3">
                      Kelola paket harian dan bulanan dari halaman membership management.
                    </p>
                    <Button as={Link} to="/membership-setting" variant="outline-success">
                      Open membership setup
                    </Button>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={6}>
                <Card className="sales-workspace__side-card h-100">
                  <Card.Body>
                    <div className="sales-workspace__side-title">
                      <FaCalendarAlt />
                      <span>Private session setup</span>
                    </div>
                    <p className="text-muted mb-3">
                      Staff, client, layanan, dan alur penjualan session diatur dari halaman ini sebelum dipakai di front desk dan kalender session.
                    </p>
                    <div className="small text-muted mb-3">
                      {ptUsers.filter(item => item.is_active).length} staff • {ptClients.length} client • {ptProducts.filter(item => item.is_active).length} layanan aktif
                    </div>
                    <Button as={Link} to="/fitness-transactions/setup/pt-setting" variant="outline-success">
                      Open private session setup
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Card className="sales-workspace__activity-card mt-4">
              <Card.Body>
                <div className="sales-workspace__activity-head">
                  <div>
                    <span className="sales-workspace__eyebrow">Active class catalog</span>
                    <h3>Class ready for front desk</h3>
                  </div>
                </div>

                <Table responsive className="mb-0 sales-workspace__table">
                  <thead>
                    <tr>
                      <th>Class</th>
                      <th>Coach</th>
                      <th>Harga</th>
                      <th>Share</th>
                      <th>Status</th>
                      <th>Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!loading && classes.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center py-4 text-muted">
                          Belum ada class untuk outlet ini.
                        </td>
                      </tr>
                    ) : null}
                    {classes.map(item => (
                      <tr key={item.id}>
                        <td>{item.class_name}</td>
                        <td>{item.coach_name || '-'}</td>
                        <td>{formatCurrency(item.unit_price)}</td>
                        <td>
                          {item.coach_share_mode}: {item.coach_share_value}
                        </td>
                        <td>{item.is_active ? 'Active' : 'Inactive'}</td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button
                              size="sm"
                              variant="outline-success"
                              onClick={() =>
                                setClassForm({
                                  id: item.id,
                                  class_name: item.class_name || '',
                                  coach_name: item.coach_name || '',
                                  unit_price: item.unit_price || '',
                                  coach_share_mode: item.coach_share_mode || 'percentage',
                                  coach_share_value: item.coach_share_value || '',
                                  is_active: !!item.is_active,
                                  notes: item.notes || '',
                                })
                              }
                            >
                              <FaEdit />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => handleDelete(item.id)}
                            >
                              <FaTrash />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>

            <Card className="sales-workspace__activity-card mt-4">
              <Card.Body>
                <div className="sales-workspace__activity-head">
                  <div>
                    <span className="sales-workspace__eyebrow">Session service catalog</span>
                    <h3>Session services shared by setup and front desk</h3>
                  </div>
                </div>

                <Row className="g-3 mb-4">
                  <Col md={4}>
                    <div className="sales-preview__stat-card">
                      <span><FaUserTie /> Staff aktif</span>
                      <strong>{ptUsers.filter(item => item.is_active).length}</strong>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="sales-preview__stat-card">
                      <span><FaUsers /> Client aktif</span>
                      <strong>{ptClients.length}</strong>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="sales-preview__stat-card">
                      <span><FaBoxOpen /> Layanan aktif</span>
                      <strong>{ptProducts.filter(item => item.is_active).length}</strong>
                    </div>
                  </Col>
                </Row>

                <Table responsive className="mb-0 sales-workspace__table">
                  <thead>
                    <tr>
                      <th>Staff</th>
                      <th>Layanan</th>
                      <th>Tipe</th>
                      <th>Sesi</th>
                      <th>Harga</th>
                      <th>Share</th>
                      <th>Status</th>
                      <th>Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ptProducts.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="text-center py-4 text-muted">
                          Belum ada layanan session untuk outlet ini.
                        </td>
                      </tr>
                    ) : null}
                    {ptProducts.map(item => (
                      <tr key={item.id}>
                        <td>{item.pt_name || '-'}</td>
                        <td>{item.product_name}</td>
                        <td>{item.product_type}</td>
                        <td>{item.session_count}</td>
                        <td>{formatCurrency(item.price)}</td>
                        <td>{item.share_mode}: {item.share_value}</td>
                        <td>{item.is_active ? 'Active' : 'Inactive'}</td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button
                              size="sm"
                              variant="outline-success"
                              onClick={() =>
                                setProductForm({
                                  id: item.id,
                                  pt_user_id: String(item.pt_user_id || ''),
                                  product_name: item.product_name || '',
                                  product_type: item.product_type || 'package',
                                  session_count: item.session_count || '1',
                                  price: item.price || '',
                                  expiry_days: item.expiry_days || '0',
                                  share_mode: item.share_mode || 'percentage',
                                  share_value: item.share_value || '',
                                  is_active: !!item.is_active,
                                  notes: item.notes || '',
                                })
                              }
                            >
                              <FaEdit />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => handleDeletePTProduct(item.id)}
                            >
                              <FaTrash />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default FitnessTransactionSetup;
